// Loading.js
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import debounce from 'lodash.debounce';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindNaverRankTracking.css';
import AuthService from '../../services/authService';
registerAllModules();

function isValidKeyword(keyword) {
    return keyword && keyword.trim().length > 0;
}

const API_PY_URL = process.env.REACT_APP_PY_API_URL || 'http://localhost:5003';

const FindNaverRankTracking = ({ title = '네이버 순위 추적' }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('');
    const cutBufferRef = useRef([]);
    const hotRef = useRef(null);
    const [tableData, setTableData] = useState([]);
    const pollingRef = useRef(null);

    const [colHeaders, setColHeaders] = useState([
        '메모1',
        '메모2',
        '메모3',
        '스토어명',
        '상품명',
        '상품 URL',
        '순위 조회 키워드 <span class="text-danger">*</span>',
        '단일 MID <span class="text-danger">*</span>',
        '원부 MID',
    ]);

    const [columns, setColumns] = useState([
        { data: 0 },
        { data: 1 },
        { data: 2 },
        { data: 3 },
        { data: 4 },
        { data: 5 },
        { data: 6 },
        { data: 7 },
        { data: 8 },
    ]);

    const [colWidths, setColWidths] = useState([100, 100, 100, 120, 180, 200, 150, 120, 120]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            setLoadingMessage('데이터를 불러오는 중입니다...');
            const response = await AuthService.authAxios.get('/product/rank/tracking/rows');

            if (response.data.success) {
                const rows = [];
                const data = response.data.data;
                if (data.length > 0) {
                    // 데이터를 sort_order 기준으로 정렬
                    const sortedData = data.sort((a, b) => a.sort_order - b.sort_order);

                    // 가장 큰 sort_order 값 찾기
                    const maxSortOrder = sortedData[sortedData.length - 1].sort_order;

                    // 0부터 maxSortOrder까지의 배열 생성

                    let currentDataIndex = 0;

                    // 0부터 maxSortOrder까지 순회하면서 데이터 또는 빈 행 추가
                    for (let i = 0; i <= maxSortOrder; i++) {
                        if (
                            currentDataIndex < sortedData.length &&
                            sortedData[currentDataIndex].sort_order === i
                        ) {
                            // sort_order가 일치하는 데이터가 있으면 추가
                            const row = sortedData[currentDataIndex];
                            rows.push([
                                row.memo1 || '',
                                row.memo2 || '',
                                row.memo3 || '',
                                row.store_name || '',
                                row.product_name || '',
                                row.product_url || '',
                                row.keyword || '',
                                row.product_id || '',
                                row.product_mid || '',
                            ]);
                            currentDataIndex++;
                        } else {
                            // sort_order가 없는 경우 빈 행 추가
                            rows.push(Array(9).fill(''));
                        }
                    }
                }
                // 최소 300행을 유지하도록 빈 행 추가
                const emptyRowsNeeded = Math.max(0, 300 - rows.length);
                const emptyRows = Array.from({ length: emptyRowsNeeded }, () => Array(9).fill(''));

                setTableData([...rows, ...emptyRows]);
                setLoadingMessage('');

                // 순위 데이터 가져오기
                const rankResults = await fetchRankResults();
                if (rankResults) {
                    applyResultToTable(rankResults);
                }
            } else {
                setError('데이터 로드 실패');
                setLoadingMessage('데이터 로드에 실패했습니다.');
            }
        } catch (err) {
            console.error('데이터 로드 에러:', err);
            setError(err.message);
            setLoadingMessage('데이터 로드 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const saveRowsToServer = async changes => {
        console.log('🔄 저장 중:', changes);
        try {
            const response = await AuthService.authAxios.post(`/product/rank/tracking/rows`, {
                changes,
            });

            // insertedRows가 있는 경우 처리
            if (
                (response.data.insertedRows && response.data.insertedRows.length > 0) ||
                (response.data.updatedRows && response.data.updatedRows.length > 0)
            ) {
                setTableData(prevData => {
                    const newData = [...prevData];

                    // // 새로 삽입된 행 처리
                    // response.data.insertedRows?.forEach(insertedRow => {
                    //     const rowIndex = insertedRow.sort_order;
                    //     if (newData[rowIndex]) {
                    //         newData[rowIndex][0] = insertedRow.id;
                    //     }
                    // });

                    // // 업데이트된 행 처리
                    // response.data.updatedRows?.forEach(updatedRow => {
                    //     const rowIndex = updatedRow.sort_order;
                    //     if (newData[rowIndex]) {
                    //         newData[rowIndex][0] = updatedRow.id;
                    //     }
                    // });

                    return newData;
                });
            }

            console.log('✅ 저장 완료:', response.data);
        } catch (error) {
            console.error('❌ 저장 실패:', error);
        }
    };

    const deleteRowsFromServer = async (sortOrders, isAllDelete = false) => {
        try {
            const response = await AuthService.authAxios.post(
                `/product/rank/tracking/delete`,
                { sortOrders, isAllDelete } // isAllDelete 플래그 추가
            );
            console.log('🗑️ 삭제 완료:', response.data);
        } catch (err) {
            console.error('❌ 삭제 실패:', err);
        }
    };

    const handleMidSearch = async () => {
        const currentData = [...tableData];
        const lines = currentData.map(
            (row, index) => `${row[6]}\t${row[7]}\t${row[8] || ''}\t${index}`
        );

        if (lines.length === 0) {
            alert('순위 조회 키워드와 단일 MID를 모두 입력한 행이 1개 이상 필요합니다.');
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage('📡 조회 요청을 서버에 전송 중입니다...');
            const user_id = AuthService.getCurrentUserId();
            const response = await axios.post(`${API_PY_URL}/api/process`, { lines, user_id });
            const requestId = response.data.request_id;
            localStorage.setItem('request_id', requestId);
            startPolling(requestId);
            applyRealTimeResultToTable(response.data, 'column');
        } catch (e) {
            setLoadingMessage('❌ 서버 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const fetchRankResults = async request_id => {
        try {
            const user_id = AuthService.getCurrentUserId();
            const response = await AuthService.authAxios.get(
                `/product/rank/tracking/rank/history?user_id=${user_id}`
            );
            if (response.data.success) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            console.error('순위 결과 조회 실패:', error);
            return null;
        }
    };

    const applyResultToTable = results => {
        const hotInstance = hotRef.current?.hotInstance;
        if (!hotInstance) return;

        // 현재 테이블 데이터와 상태
        const currentData = hotInstance.getSourceData();
        const sessionData = results;

        // 현재 열 인덱스부터 시작
        let newIndex = colHeaders.length;

        // 기존 상태 복사
        let updatedHeaders = [...colHeaders];
        let updatedColumns = [...columns];
        let updatedWidths = [...colWidths];
        const updatedData = [...currentData]; // 실제로는 참조지만 구조상 문제 없음

        sessionData.forEach(session => {
            // 헤더명: 시작 시간에서 "YY-MM-DD HH:MM" 형태 추출
            const newHeader = `${session.started_at.substring(2, 16)}|${session.request_id}`;
            const newColumn = { data: newIndex };

            // 열 정보 누적
            updatedHeaders.push(newHeader);
            updatedColumns.push(newColumn);
            updatedWidths.push(120);

            // 행 위치에 값 삽입 (rank 또는 status)
            session.rank.forEach(rankItem => {
                const rowIndex = rankItem.sort_order;
                if (rowIndex >= 0 && rowIndex < updatedData.length) {
                    let value = rankItem.rank != null ? rankItem.rank : rankItem.status || '';
                    value = value == 'norank' ? '확인 불가' : value;
                    updatedData[rowIndex][newIndex] = value;
                }
            });

            newIndex += 1;
        });

        // 최종 적용 (한 번에)
        setColHeaders(updatedHeaders);
        setColumns(updatedColumns);
        setColWidths(updatedWidths);
        setTableData(updatedData);

        hotRef.current?.hotInstance?.render();
    };

    const applyRealTimeResultToTable = (results, type) => {
        const hotInstance = hotRef.current?.hotInstance;
        if (!hotInstance) return;

        const insertIndex = 9;

        if (type == 'column') {
            const newColumnDataIndex = columns.length;

            // 🔧 열 수가 정확히 맞는 새로운 행 생성
            const expectedColumnCount = columns.length + 1; // 새 열 포함
            const updatedData = tableData.map(row => {
                const newRow = [...row];
                newRow.splice(insertIndex, 0, ''); // 중간 삽입
                while (newRow.length < expectedColumnCount) {
                    newRow.push('');
                }
                return newRow;
            });

            // 헤더
            const updatedHeaders = [
                ...colHeaders.slice(0, insertIndex),
                `${results.startDate.substring(2, 16)}|${results.request_id}`,
                ...colHeaders.slice(insertIndex),
            ];

            // columns
            const updatedColumns = [
                ...columns.slice(0, insertIndex),
                { data: 9 },
                ...columns.slice(insertIndex).map(col => ({
                    ...col,
                    data: typeof col.data === 'number' ? col.data + 1 : col.data,
                })),
            ];

            const updatedWidths = [
                ...colWidths.slice(0, insertIndex),
                120,
                ...colWidths.slice(insertIndex),
            ];

            setColHeaders(updatedHeaders);
            setColumns(updatedColumns);
            setColWidths(updatedWidths);
            setTableData(updatedData);
        } else if (type == 'rank') {
            const currentData = hotInstance.getSourceData();
            const updatedData = [...currentData]; // 실제로는 참조지만 구조상 문제 없음

            results.forEach(result => {
                const value = result.rank;
                updatedData[result.sort_order][insertIndex] = value;
            });

            setTableData(updatedData);
        }
    };

    const startPolling = request_id => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        setLoadingMessage('🔄 서버에서 결과를 조회 중입니다... 화면을 나가면 조회가 취소됩니다.');

        pollingRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API_PY_URL}/api/status/${request_id}`);
                const { status, result } = res.data;
                if (status === 'done') {
                    clearInterval(pollingRef.current);
                    localStorage.removeItem('request_id');
                    applyRealTimeResultToTable(result, 'rank');
                    setLoadingMessage('✅ 순위 조회가 완료되었습니다.');
                }
            } catch (e) {
                clearInterval(pollingRef.current);
                localStorage.removeItem('request_id');
            }
        }, 2000);
    };

    // const applyResultToTable = results => {
    //     const newData = [...tableData];
    //     results.forEach(res => {
    //         const index = newData.findIndex(
    //             row => row[6] === res.keyword && row[7] === res.product_id
    //         );
    //         if (index !== -1) {
    //             // newData[index][8] = res.parent_mid || newData[index][8];
    //             // newData[index][7] = res.product_id || newData[index][7];
    //             // newData[index][6] = res.keyword || newData[index][6];
    //             // newData[index][5] = res.product_url || newData[index][5];
    //             // newData[index][4] = res.product_name || newData[index][4];
    //             // newData[index][3] = res.store_name || newData[index][3];
    //             // newData[index][2] = res.memo3 || newData[index][2];
    //             // newData[index][1] = res.memo2 || newData[index][1];
    //             // newData[index][0] = res.memo1 || newData[index][0];
    //         }
    //     });
    //     setTableData(newData);
    //     hotRef.current?.hotInstance?.render();
    // };

    useEffect(() => {
        fetchInitialData(); // 컴포넌트 마운트 시 데이터 로드
        const request_id = localStorage.getItem('request_id');
        // const user_id = getCurrentUserId(); // 또는 JWT에서 추출
        if (request_id) startPolling(request_id);
    }, []);

    const handleSubmit = e => {
        e.preventDefault();
        handleMidSearch();
    };

    return (
        <>
            {/* <!-- Begin page --> */}
            <div id="wrapper">
                <Navi title={title} />

                <LeftSide />

                <div className="content-page">
                    <div className="content">
                        {/* <!-- Start Content--> */}
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <form onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <h4 className="card-title">✔️ 안내사항</h4>
                                                <p>
                                                    ▪️ 네이버 상품만 조회 가능합니다. (최대 300개)
                                                    <br />
                                                    ▪️ 순위 조회를 위해 키워드와 단일 MID만
                                                    입력하시면 됩니다.
                                                    <br />
                                                    ▪️ 스케줄링은 다음 업데이트에 적용됩니다.
                                                </p>
                                                {/* <p class="text-muted">
                                                    상품페이지 URL을 https부터 입력하세요.
                                                    <br />
                                                    예시{')'}
                                                    https://smartstore.naver.com/tsad/products/12345678901
                                                </p> */}
                                                {/* <div className="spinner-border text-primary"></div> */}
                                                <div className="button-list mb-2 text-center">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary waves-effect waves-light"
                                                    >
                                                        {loading && (
                                                            <span
                                                                className="spinner-border spinner-border-sm me-1"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></span>
                                                        )}
                                                        조회
                                                    </button>
                                                </div>

                                                <div className="text-center my-2">
                                                    <span
                                                        style={{ fontSize: '14px', color: '#555' }}
                                                    >
                                                        {loadingMessage}
                                                    </span>
                                                </div>

                                                <div
                                                    className="ht-theme-main-dark-auto"
                                                    style={{ height: '70vh', width: '100%' }}
                                                >
                                                    <HotTable
                                                        ref={hotRef}
                                                        data={tableData}
                                                        colHeaders={colHeaders.map(header =>
                                                            header.includes('|')
                                                                ? header.split('|')[0]
                                                                : header
                                                        )}
                                                        columns={columns}
                                                        colWidths={colWidths}
                                                        rowHeaders={true}
                                                        autoWrapRow={true}
                                                        autoWrapCol={true}
                                                        selectionMode="row"
                                                        contextMenu={{
                                                            callback: async (key, selection) => {
                                                                if (key !== 'remove_col_custom')
                                                                    return;

                                                                const startCol =
                                                                    selection[0]?.start?.col;
                                                                const endCol =
                                                                    selection[0]?.end?.col;
                                                                const [from, to] = [
                                                                    Math.min(startCol, endCol),
                                                                    Math.max(startCol, endCol),
                                                                ];

                                                                if (from < 9) {
                                                                    alert(
                                                                        '❌ 기본 열은 삭제할 수 없습니다.'
                                                                    );
                                                                    return;
                                                                }

                                                                const requestIds = [];
                                                                for (let i = from; i <= to; i++) {
                                                                    const header = colHeaders[i];
                                                                    const requestId =
                                                                        header.includes('|')
                                                                            ? header.split('|')[1]
                                                                            : null;
                                                                    if (requestId)
                                                                        requestIds.push(requestId);
                                                                }

                                                                if (requestIds.length === 0) {
                                                                    alert(
                                                                        '삭제할 수 있는 순위 데이터가 없습니다.'
                                                                    );
                                                                    return;
                                                                }

                                                                if (
                                                                    !window.confirm(
                                                                        `${requestIds.length}개 열을 삭제하시겠습니까?`
                                                                    )
                                                                )
                                                                    return;

                                                                try {
                                                                    await AuthService.authAxios.post(
                                                                        '/product/rank/tracking/delete-column',
                                                                        { request_ids: requestIds }
                                                                    );
                                                                } catch (err) {
                                                                    console.error(
                                                                        '❌ 서버 삭제 실패:',
                                                                        err
                                                                    );
                                                                }

                                                                // 스냅샷 유지
                                                                const newHeaders = [...colHeaders];
                                                                const newWidths = [...colWidths];
                                                                const newColumns = [...columns];

                                                                // 🔧 반드시 역순으로 splice
                                                                for (let i = to; i >= from; i--) {
                                                                    newHeaders.splice(i, 1);
                                                                    newWidths.splice(i, 1);
                                                                    newColumns.splice(i, 1);
                                                                }

                                                                setColHeaders(newHeaders);
                                                                setColWidths(newWidths);
                                                                setColumns(newColumns);
                                                            },
                                                            items: {
                                                                row_above: {
                                                                    name: '위로 행 추가',
                                                                },
                                                                row_below: {
                                                                    name: '아래로 행 추가',
                                                                },
                                                                remove_row: {
                                                                    name: '선택 행 삭제',
                                                                },
                                                                undo: {
                                                                    name: '되돌리기',
                                                                },
                                                                redo: {
                                                                    name: '다시 실행하기',
                                                                },
                                                                copy: {
                                                                    name: '복사',
                                                                },
                                                                cut: {
                                                                    name: '잘라내기',
                                                                },
                                                                remove_col_custom: {
                                                                    name: '선택 열 삭제',
                                                                    hidden: function () {
                                                                        // 선택된 위치가 row header(좌측 검정색)일 경우 숨김
                                                                        const hot =
                                                                            hotRef.current
                                                                                ?.hotInstance;
                                                                        const selected =
                                                                            hot?.getSelectedRange();
                                                                        if (
                                                                            !selected ||
                                                                            !selected[0]
                                                                        )
                                                                            return true;

                                                                        const fromCol =
                                                                            selected[0].from.col;
                                                                        const toCol =
                                                                            selected[0].to.col;
                                                                        return (
                                                                            fromCol === -1 ||
                                                                            toCol === -1
                                                                        ); // -1이면 행 헤더 클릭
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        stretchH="all"
                                                        height="100%"
                                                        width="100%"
                                                        cells={(row, col) => {
                                                            let classNames = 'htWrap htMiddle';

                                                            if (col >= 0 && col <= 8) {
                                                                classNames += ' gray-column';
                                                            }

                                                            return { className: classNames };
                                                        }}
                                                        beforeRemoveCol={(
                                                            index,
                                                            amount,
                                                            physicalColumns
                                                        ) => {
                                                            if (index < 9) {
                                                                alert(
                                                                    '❌ 기본 열은 삭제할 수 없습니다.'
                                                                );
                                                                return false;
                                                            }
                                                            return true;
                                                        }}
                                                        beforeCut={(data, coords) => {
                                                            const buffer = [];
                                                            const coord = coords[0]; // 실제 잘라낸 영역 범위
                                                            for (
                                                                let row = coord.startRow;
                                                                row <= coord.endRow;
                                                                row++
                                                            ) {
                                                                for (
                                                                    let col = coord.startCol;
                                                                    col <= coord.endCol;
                                                                    col++
                                                                ) {
                                                                    const oldValue =
                                                                        tableData?.[row]?.[col] ??
                                                                        '';
                                                                    buffer.push({
                                                                        row,
                                                                        col,
                                                                        oldValue,
                                                                    });
                                                                }
                                                            }
                                                            cutBufferRef.current = buffer;
                                                            console.log(
                                                                '📌 cutBufferRef full:',
                                                                buffer
                                                            );
                                                        }}
                                                        beforeChange={(changes, source) => {
                                                            if (!changes) return;

                                                            // 붙여넣기 또는 기타 변경으로 인해 300개 초과될 가능성 있을 때 차단
                                                            const isPaste =
                                                                source === 'CopyPaste.paste';
                                                            if (isPaste) {
                                                                const rowCount = tableData.length;
                                                                const maxRowTouched = Math.max(
                                                                    ...changes.map(([row]) => row)
                                                                );
                                                                const predictedRowCount =
                                                                    maxRowTouched + 1;

                                                                if (predictedRowCount > 300) {
                                                                    alert(
                                                                        '⚠️ 최대 300개 행까지만 입력할 수 있습니다.'
                                                                    );
                                                                    return false;
                                                                }
                                                            }

                                                            // // 변경 시도 중 9번째(인덱스 9) 이후 열이면 제거
                                                            // const protectedColumns = changes.filter(([row, col]) => col >= 9);
                                                            // if (protectedColumns.length > 0) {
                                                            //   alert('❗ 순위 조회 열은 삭제가 허용되지 않습니다 좌측 가로 행 번호를 마우스 오른쪽 버튼 팝업 메뉴를 통해 삭제하세요.');
                                                            //   return false; // 변경 자체를 막음
                                                            // }
                                                        }}
                                                        afterChange={(changes, source) => {
                                                            if (
                                                                !changes ||
                                                                ![
                                                                    'edit',
                                                                    'CopyPaste.paste',
                                                                    'UndoRedo.undo',
                                                                ].includes(source)
                                                            )
                                                                return;

                                                            // 해당 행의 모든 셀이 비어있는지 확인
                                                            console.log(
                                                                '🔄 전체 테이블 데이터1:',
                                                                source
                                                            );
                                                            console.log(
                                                                '🔄 전체 테이블 데이터2:',
                                                                tableData
                                                            );
                                                            const isAllRowsEmpty = tableData.every(
                                                                rowData =>
                                                                    rowData.every(cell => {
                                                                        // null, undefined 체크
                                                                        if (cell == null)
                                                                            return true;
                                                                        // 숫자를 문자열로 변환
                                                                        const strCell =
                                                                            String(cell);
                                                                        return (
                                                                            strCell.trim() === ''
                                                                        );
                                                                    })
                                                            );

                                                            // 전체 삭제인 경우 해당 행 삭제 처리
                                                            if (isAllRowsEmpty) {
                                                                console.log('🔄 전체 삭제 감지:');
                                                                const emptyRowIndex = changes[0][0]; // 비어있는 행의 인덱스
                                                                deleteRowsFromServer(
                                                                    [emptyRowIndex],
                                                                    true
                                                                ); // 전체 삭제 플래그를 true로 설정
                                                                return;
                                                            }

                                                            // 직접 수정되거나 붙여넣기 된 셀들 정리
                                                            const formattedChanges = changes.map(
                                                                ([row, col, oldVal, newVal]) => ({
                                                                    row,
                                                                    col,
                                                                    oldValue: oldVal,
                                                                    newValue: newVal,
                                                                })
                                                            );

                                                            // cutBuffer 기반으로 비워진 셀 찾기
                                                            const clearedCells =
                                                                cutBufferRef.current
                                                                    .filter(({ row, col }) => {
                                                                        const currentVal =
                                                                            tableData?.[row]?.[
                                                                                col
                                                                            ] ?? '';
                                                                        return currentVal === '';
                                                                    })
                                                                    .map(
                                                                        ({
                                                                            row,
                                                                            col,
                                                                            oldValue,
                                                                        }) => ({
                                                                            row,
                                                                            col,
                                                                            oldValue,
                                                                            newValue: '',
                                                                        })
                                                                    );

                                                            // 초기화
                                                            cutBufferRef.current = [];

                                                            // 합쳐서 서버로 전송
                                                            const totalChanges = [
                                                                ...formattedChanges,
                                                                ...clearedCells,
                                                            ];
                                                            console.log(
                                                                '🔄 저장 중:',
                                                                totalChanges
                                                            );
                                                            saveRowsToServer(totalChanges);
                                                        }}
                                                        afterRemoveRow={(
                                                            index,
                                                            amount,
                                                            physicalRows
                                                        ) => {
                                                            const deletedSortOrders = physicalRows;
                                                            console.log(
                                                                '🔄 삭제 중:',
                                                                deletedSortOrders
                                                            );

                                                            // 1. 서버에 삭제 요청
                                                            deleteRowsFromServer(deletedSortOrders);

                                                            // 2. 삭제 이후 남은 데이터는 prevData에 이미 반영되어 있음
                                                            setTableData(prevData => {
                                                                console.log(
                                                                    '🔄 삭제 후:',
                                                                    prevData
                                                                        .filter(row =>
                                                                            row.every(
                                                                                cell => cell !== ''
                                                                            )
                                                                        )
                                                                        .slice(-1)
                                                                );
                                                                // 3. sort_order를 0부터 재정렬한 내용으로 save 요청
                                                                const sortOrderChanges =
                                                                    prevData.map((row, idx) => ({
                                                                        row: idx,
                                                                        oldValue: null,
                                                                        newValue: idx,
                                                                    }));

                                                                console.log(
                                                                    '🔄 sort_order 업데이트 중:',
                                                                    sortOrderChanges
                                                                );

                                                                const emptyRowsNeeded = Math.max(
                                                                    0,
                                                                    300 - prevData.length
                                                                );
                                                                const emptyRows = Array.from(
                                                                    { length: emptyRowsNeeded },
                                                                    () => Array(9).fill('')
                                                                );

                                                                return [...prevData, ...emptyRows];
                                                            });
                                                        }}
                                                        beforeRemoveRow={(
                                                            index,
                                                            amount,
                                                            physicalRows
                                                        ) => {
                                                            const confirmed = window.confirm(
                                                                `정말로 삭제하시겠습니까?\n\n삭제된 행은 복원할 수 없으며,\n조회된 순위 데이터도 다시 확인할 수 없습니다.`
                                                            );

                                                            if (!confirmed) {
                                                                return false; // 삭제 취소
                                                            }

                                                            // 삭제 허용
                                                            return true;
                                                        }}
                                                        beforeCreateRow={(
                                                            index,
                                                            amount,
                                                            source
                                                        ) => {
                                                            if (tableData.length + amount > 300) {
                                                                alert(
                                                                    '⚠️ 최대 300개 행까지만 생성할 수 있습니다.'
                                                                );
                                                                return false;
                                                            }

                                                            console.log(
                                                                '🔄 beforeCreateRow source:',
                                                                source
                                                            );
                                                            // source가 'ContextMenu.insertRow'일 때만 처리 (컨텍스트 메뉴를 통한 삽입)
                                                            if (
                                                                source === 'ContextMenu.rowAbove' ||
                                                                source === 'ContextMenu.rowBelow'
                                                            ) {
                                                                const position = source.includes(
                                                                    'rowAbove'
                                                                )
                                                                    ? 'rowAbove'
                                                                    : 'rowBelow';

                                                                // 서버에 요청
                                                                AuthService.authAxios
                                                                    .post(
                                                                        '/product/rank/tracking/insert-row',
                                                                        {
                                                                            position,
                                                                            index,
                                                                        }
                                                                    )
                                                                    .then(response => {
                                                                        if (response.data.success) {
                                                                            // 성공 시 서버에서 반환한 sort_order로 행 추가
                                                                            // 여기서는 기본 행 추가 동작을 허용
                                                                            return true;
                                                                        } else {
                                                                            console.error(
                                                                                '행 삽입 실패:',
                                                                                response.data
                                                                                    .message
                                                                            );
                                                                            return false; // 행 추가 취소
                                                                        }
                                                                    })
                                                                    .catch(error => {
                                                                        console.error(
                                                                            '행 삽입 에러:',
                                                                            error
                                                                        );
                                                                        return false; // 행 추가 취소
                                                                    });
                                                            }
                                                            return true; // 다른 소스의 행 추가는 허용
                                                        }}
                                                        licenseKey="non-commercial-and-evaluation"
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                {/* <!-- end col --> */}
                            </div>
                            {/* <!-- end row --> */}
                        </div>
                        {/* <!-- container-fluid --> */}
                    </div>
                    {/* <!-- content --> */}
                </div>
            </div>

            <RightBar />

            <div className="rightbar-overlay"></div>
        </>
    );
};

export { FindNaverRankTracking };
